// V8 Shadow — page d'observation admin. Lecture seule : aucun réglage, aucune activation, replay strictement dry-run.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, Play, ArrowSquareOut, WarningCircle, Database } from "@phosphor-icons/react";

const FILTERS = {
  scope: ["profil", "projet"],
  status: ["complet", "partiel", "non_calculable"],
  faisabilite_level: ["basse", "moyenne", "haute"],
  priorite_band: ["basse", "moyenne", "haute", "critique"],
};
const FILTER_LABEL = { scope: "Scope", status: "Statut", faisabilite_level: "Faisabilité", priorite_band: "Priorité" };
const BAND = { basse: "bg-slate-100 text-slate-600", moyenne: "bg-amber-100 text-amber-800", haute: "bg-emerald-100 text-emerald-800", critique: "bg-red-100 text-red-800" };

function count(rows, key, fallback = "—") {
  const c = {};
  rows.forEach((r) => { const k = r[key] ?? fallback; c[k] = (c[k] || 0) + 1; });
  return c;
}
function Dist({ title, data, order, testid }) {
  const keys = order ? [...order.filter((k) => k in data), ...Object.keys(data).filter((k) => !order.includes(k))] : Object.keys(data).sort((a, b) => data[b] - data[a]);
  const total = Object.values(data).reduce((s, v) => s + v, 0) || 1;
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="overline mb-2" style={{ color: "var(--cf-text-2)" }}>{title}</div>
      {keys.length === 0 && <div className="text-sm text-slate-400">Aucune donnée</div>}
      <ul className="space-y-1.5">
        {keys.map((k) => (
          <li key={k} className="text-[13px]">
            <div className="flex justify-between"><span className="font-mono text-slate-700">{k}</span><span className="font-bold text-slate-900" data-testid={`${testid}-${k}`}>{data[k]}</span></div>
            <div className="h-1 rounded bg-slate-100 mt-0.5"><div className="h-1 rounded bg-slate-800" style={{ width: `${(data[k] / total) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
}
function Kpi({ label, value, testid, tone }) {
  return (
    <div className={`rounded-xl border p-3 ${tone || "bg-white border-slate-200"}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="text-xl font-extrabold text-slate-900">{value ?? "—"}</div>
    </div>
  );
}
function CaseList({ title, rows, testid, onOpen, me }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex justify-between items-center mb-2"><div className="overline" style={{ color: "var(--cf-text-2)" }}>{title}</div><span className="text-xs font-bold text-slate-700">{rows.length}</span></div>
      {rows.length === 0 ? <div className="text-sm text-slate-400">Aucun cas</div> : (
        <ul className="divide-y divide-slate-100">
          {rows.slice(0, 20).map((r, i) => (
            <li key={`${r.simulation_id || r.dossier_id}-${i}`} className="py-1.5 flex items-center justify-between gap-2 text-[13px]">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{r.label}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">{r.error || `${r.status || "—"} · F ${r.faisabilite_score ?? "null"} · P ${r.priorite_score ?? "null"} · ${r.action || ""}${r.next_actions?.length ? " → " + r.next_actions.join(", ") : ""}`}</div>
              </div>
              {r.dossier_id && r.dossier_exists && r.courtier_id === me ? (
                <button type="button" onClick={() => onOpen(r.dossier_id)} className="shrink-0 text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs font-bold" data-testid={`case-open-${r.dossier_id}`}>
                  Ouvrir <ArrowSquareOut size={12} />
                </button>
              ) : <span className="shrink-0 text-[11px] text-slate-400">{r.dossier_exists ? "autre courtier" : "dossier supprimé"}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminScoringV8() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stored, setStored] = useState(null);
  const [replay, setReplay] = useState(null);
  const [view, setView] = useState("stored");
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({});

  useEffect(() => { if (!authLoading && (!user || !user.is_admin)) navigate("/", { replace: true }); }, [user, authLoading, navigate]);
  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/scoring/overview").then((r) => setStored(r.data)).catch(() => toast.error("Lecture des résultats Shadow impossible."));
  }, [user?.is_admin]);

  const runReplay = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/scoring/replay?limit=2000&mode=now"); setReplay(r.data); setView("replay"); toast.success(`Replay dry-run : ${r.data.population.scored} simulations analysées — aucune donnée modifiée`); }
    catch { toast.error("Replay impossible."); }
    finally { setBusy(false); }
  };

  const data = view === "replay" ? replay : stored;
  const rows = useMemo(() => (data?.rows || []).filter((r) => !r.error && Object.entries(filters).every(([k, v]) => !v || r[k] === v)), [data, filters]);
  const errorRows = useMemo(() => (data?.rows || []).filter((r) => r.error), [data]);
  const flags = useMemo(() => { const c = {}; rows.forEach((r) => r.flags.forEach((f) => { c[f] = (c[f] || 0) + 1; })); return Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 15); }, [rows]);
  const status = useMemo(() => count(rows, "status"), [rows]);

  if (authLoading || !user?.is_admin) return null;
  const eng = data?.engine || stored?.engine;

  return (
    <Layout title="Scoring V8 — Shadow" subtitle="Observation admin · lecture seule" backTo="/">
      <div className="max-w-6xl mx-auto space-y-5" data-testid="admin-scoring-v8">
        <div className="rounded-2xl p-4 border-2 border-dashed border-slate-400 bg-slate-50 flex items-start gap-3" data-testid="shadow-banner">
          <Eye size={20} weight="fill" className="text-slate-700 shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] uppercase tracking-widest font-extrabold text-slate-800">SHADOW MODE — n'influence pas le pilotage actuel</div>
            <div className="text-[13px] text-slate-600 mt-1">Aucun réglage possible ici. Les paramètres sont versionnés dans le code. Le replay est une analyse en mémoire : <b>aucune donnée modifiée</b>.</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="engine-state">
          <Kpi label="Mode" value={eng?.mode} testid="engine-mode" tone={eng?.mode === "shadow" ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"} />
          <Kpi label="scoring_version" value={eng?.scoring_version} testid="engine-scoring-version" />
          <Kpi label="config_version" value={eng?.config_version} testid="engine-config-version" />
          <Kpi label="engine_revision" value={eng?.engine_revision} testid="engine-revision" />
          <Kpi label="Événements" value={stored?.events_count} testid="engine-events" />
        </div>
        <div className="text-[12px] text-slate-500 font-mono" data-testid="batch-state">
          Batch quotidien : {eng?.batch_hour_paris ?? 6}:00 Europe/Paris · prochain {eng?.next_batch_at ? new Date(eng.next_batch_at).toLocaleString("fr-FR") : "—"} · dernier : {stored?.last_batch ? `${stored.last_batch._id} (${stored.last_batch.status}, ${stored.last_batch.scanned ?? 0} scannés, ${stored.last_batch.written ?? 0} écrits, ${stored.last_batch.errors ?? 0} erreurs)` : "aucun"}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="flex gap-1 rounded-full bg-slate-100 p-1 text-[12px] font-bold" data-testid="view-toggle">
            <button type="button" onClick={() => setView("stored")} className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${view === "stored" ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid="view-stored"><Database size={12} /> Stocké</button>
            <button type="button" onClick={() => replay && setView("replay")} disabled={!replay} className={`px-3 py-1.5 rounded-full ${view === "replay" ? "bg-white shadow text-slate-900" : "text-slate-500"} disabled:opacity-40`} data-testid="view-replay">Replay {replay ? `(${new Date(replay.computed_at).toLocaleTimeString("fr-FR")})` : ""}</button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-500">Analyse uniquement — aucune donnée modifiée.</span>
            <button type="button" onClick={runReplay} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white font-extrabold text-[13px] disabled:opacity-50" data-testid="replay-dry-run-btn">
              <Play size={14} weight="fill" /> {busy ? "Analyse…" : "Replay dry-run"}
            </button>
          </div>
        </div>

        {view === "replay" && replay?.timing_ms && (
          <div className="text-[12px] text-slate-600 font-mono" data-testid="replay-timing">
            calcul pur : avg {replay.timing_ms.avg_ms} ms · p95 {replay.timing_ms.p95_ms} ms · max {replay.timing_ms.max_ms} ms · mode {replay.mode} · dry_run={String(replay.dry_run)} persisted={String(replay.persisted)}
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="filters">
          <button type="button" onClick={() => setFilters({})} className={`px-3 py-1 rounded-full text-[12px] font-bold border ${Object.values(filters).every((v) => !v) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid="filter-all">Tous</button>
          {Object.entries(FILTERS).map(([k, vals]) => vals.map((v) => (
            <button key={`${k}-${v}`} type="button" onClick={() => setFilters((f) => ({ ...f, [k]: f[k] === v ? null : v }))}
              className={`px-3 py-1 rounded-full text-[12px] font-bold border ${filters[k] === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`filter-${k}-${v}`}>
              {FILTER_LABEL[k]} · {v.replace("_", " ")}
            </button>
          )))}
        </div>

        {!data ? <div className="text-slate-400 text-sm animate-pulse py-8 text-center">Chargement…</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="population">
              <Kpi label="Analysés" value={rows.length} testid="pop-total" />
              <Kpi label="Calculables" value={status.complet || 0} testid="pop-calculable" />
              <Kpi label="Partiels" value={status.partiel || 0} testid="pop-partiel" />
              <Kpi label="Non calculables" value={status.non_calculable || 0} testid="pop-non-calculable" />
              <Kpi label="Erreurs moteur" value={errorRows.length} testid="pop-errors" tone={errorRows.length ? "bg-red-50 border-red-200" : undefined} />
              <Kpi label="Sources" value={Object.entries(count(rows, "source")).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"} testid="pop-sources" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Dist title="Faisabilité" data={count(rows, "faisabilite_level", "non_calculable")} order={["basse", "moyenne", "haute", "non_calculable"]} testid="dist-faisabilite" />
              <Dist title="Scope" data={count(rows, "scope")} order={["profil", "projet"]} testid="dist-scope" />
              <Dist title="Confiance" data={count(rows, "confidence")} order={["haute", "moyenne", "faible"]} testid="dist-confidence" />
              <Dist title="Priorité" data={count(rows, "priorite_band", "terminal")} order={["basse", "moyenne", "haute", "critique", "terminal"]} testid="dist-priorite" />
              <Dist title="Actions — Maintenant (primary)" data={count(rows, "action")} testid="dist-actions" />
              <Dist title="Actions — Ensuite (next)" data={(() => { const c = {}; rows.forEach((r) => (r.next_actions || []).forEach((n) => { c[n] = (c[n] || 0) + 1; })); return c; })()} testid="dist-next-actions" />
              <Dist title="Top flags" data={Object.fromEntries(flags)} testid="dist-flags" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cases">
              <CaseList title="Non calculable" rows={rows.filter((r) => r.status === "non_calculable")} testid="cases-non-calculable" onOpen={(id) => navigate(`/dossier/${id}`)} me={user.user_id} />
              <CaseList title="Confiance faible" rows={rows.filter((r) => r.confidence === "faible")} testid="cases-confidence-faible" onOpen={(id) => navigate(`/dossier/${id}`)} me={user.user_id} />
              <CaseList title="Statut partiel" rows={rows.filter((r) => r.status === "partiel")} testid="cases-partiel" onOpen={(id) => navigate(`/dossier/${id}`)} me={user.user_id} />
              <CaseList title="Priorité critique" rows={rows.filter((r) => r.priorite_band === "critique")} testid="cases-critique" onOpen={(id) => navigate(`/dossier/${id}`)} me={user.user_id} />
              <CaseList title="Erreur moteur" rows={errorRows} testid="cases-erreur" onOpen={(id) => navigate(`/dossier/${id}`)} me={user.user_id} />
            </div>
            {rows.length === 0 && view === "stored" && (
              <div className="rounded-2xl bg-white border border-slate-200 py-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2" data-testid="stored-empty">
                <WarningCircle size={16} /> Aucun résultat Shadow stocké pour l'instant. Lance un replay dry-run pour une analyse à l'instant T.
              </div>
            )}
          </>
        )}
        <div className="text-[11px] text-slate-400 pb-6" data-testid="band-legend">Bandes : {Object.keys(BAND).map((b) => <span key={b} className={`pill mx-1 ${BAND[b]}`}>{b}</span>)}</div>
      </div>
    </Layout>
  );
}
